import { useEffect, useRef } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { TypingIndicator } from "./typing-indicator";
import { MessageBubble } from "./message-bubble";
import { MessagesListTypes } from "../types";
import { useScrollDetection } from "../hooks/useScrollDetection";
import { useActionsTrack } from "@/_old/features/analytics";

export function MessagesList({
  messages,
  isLoading,
  endRef,
  startRef,
  userInitials,
  onSuggestionClick,
  formHandlers,
  shouldAutoScrollRef,
  messagesCountBeforeEmailPanel,
  emailValidationPanel,
  welcomeHubPicker,
}: MessagesListTypes) {
  const splitAt =
    messagesCountBeforeEmailPanel != null && emailValidationPanel != null
      ? messagesCountBeforeEmailPanel
      : null;
  const messagesBeforePanel =
    splitAt != null ? messages.slice(0, splitAt) : messages;
  const messagesAfterPanel = splitAt != null ? messages.slice(splitAt) : [];
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasTrackedScrollRef = useRef(false);
  const { isScrollingUp, isViewingHistory, scrollContainerRef } =
    useScrollDetection(100);
  const { onActionView } = useActionsTrack();

  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]",
      );
      if (viewport && scrollContainerRef.current !== viewport) {
        (scrollContainerRef as React.MutableRefObject<HTMLDivElement>).current =
          viewport as HTMLDivElement;
      }
    }
  }, [scrollContainerRef]);

  useEffect(() => {
    if (shouldAutoScrollRef) {
      if (isViewingHistory) {
        shouldAutoScrollRef.current = false;

        if (!hasTrackedScrollRef.current) {
          onActionView("CHATBOT_SCROLL_VIEW_HISTORY");
          hasTrackedScrollRef.current = true;
        }
      } else if (!isScrollingUp) {
        shouldAutoScrollRef.current = true;
      }
    }
  }, [isViewingHistory, isScrollingUp, shouldAutoScrollRef, onActionView]);

  return (
    <ScrollArea
      // O Radix envolve o conteúdo num div display:table; min-width:100% (shrink-to-fit
      // pelo conteúdo mais largo) — uma tabela com células nowrap "vaza" a largura e corta
      // a conversa. Forçar display:block nesse div faz o layout normal valer, então a tabela
      // rola dentro do painel (via overflow-x-auto) em qualquer largura. Raiz do problema,
      // sem max-width fixo.
      // Sem `overflow-y-auto` aqui: o ScrollArea já rola pelo Viewport do Radix
      // (overflow-y: scroll). Ter o auto também na raiz cria dois scrolls verticais
      // aninhados — some enquanto o conteúdo cabe, mas ao abrir a seção do WhatsApp
      // (ou qualquer conteúdo mais alto) os dois rolam juntos e o curso dobra.
      className="h-full overscroll-contain px-[24px] pt-[18px] [&_[data-radix-scroll-area-viewport]>div]:!block"
      ref={scrollAreaRef}
    >
      <div className="min-w-0 space-y-4 min-h-full">
        <div ref={startRef} />
        {welcomeHubPicker}
        {messagesBeforePanel.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            userInitials={userInitials}
            onSuggestionClick={onSuggestionClick}
            formHandlers={formHandlers}
          />
        ))}

        {emailValidationPanel && (
          <div className="pt-2">{emailValidationPanel}</div>
        )}

        {messagesAfterPanel.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            userInitials={userInitials}
            onSuggestionClick={onSuggestionClick}
            formHandlers={formHandlers}
          />
        ))}

        {/* Only show TypingIndicator if last message is empty (streaming hasn't started) */}
        {isLoading &&
          (!messages.length || !messages[messages.length - 1]?.content) && (
            <TypingIndicator />
          )}

        <div ref={endRef} />
      </div>
    </ScrollArea>
  );
}
